import { useEffect, useState } from "react";
import { Loader2, Plus, Save, Trash2, Truck, FileText } from "lucide-react";
import { toast } from "sonner";
import { errText, papi } from "../portalApi";
import { inr, Margin, NoCopy, SheetButtons } from "./ListTools";
import { ShipMarkup } from "./ShipMarkup";

const sheetPdf = async (c) => {
  try {
    const b = await papi("get", `/dropship/clients/${c.id}/sheet.pdf`, null, { responseType: "blob" });
    const u = URL.createObjectURL(b);
    const a = document.createElement("a");
    a.href = u; a.download = `${c.name.replace(/[^\w-]+/g, "_")}_price_list.pdf`; a.click();
    setTimeout(() => URL.revokeObjectURL(u), 2000);
  } catch (e) { toast.error(errText(e)); }
};

const k3 = (x) => `${x.service}|${x.country}|${x.w_from ?? ""}|${x.w_to ?? ""}`;

/* Hub: the shipping a dropship client is charged — cost from the office's Shipping prices, sale set by the partner. */
export const ShipList = ({ c }) => {
  const [rows, setRows] = useState(c.ship_rates || []);
  const [opt, setOpt] = useState({ services: [], countries: [] });
  const [pick, setPick] = useState({ service: "", country: "" });
  const [costs, setCosts] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => setRows(c.ship_rates || []), [c.id]);
  const refresh = () => papi("get", "/dropship/clients").then((d) => { const x = (d.clients || d).find?.((y) => y.id === c.id); if (x) setRows(x.ship_rates || []); }).catch(() => {});
  useEffect(() => { papi("get", "/shipping/options").then(setOpt).catch(() => {}); }, []);
  useEffect(() => {
    const pairs = [...new Set(rows.map((r) => `${r.service}|${r.country}`))].filter((p) => !(p in costs));
    pairs.slice(0, 30).forEach((p) => {
      const [service, country] = p.split("|");
      papi("get", `/shipping/options?service=${encodeURIComponent(service)}&country=${encodeURIComponent(country)}`)
        .then((d) => setCosts((s) => ({ ...s, [p]: Object.fromEntries((d.weights || []).map((w) => [k3(w), w])) }))).catch(() => setCosts((s) => ({ ...s, [p]: {} })));
    });
  }, [rows]);
  useEffect(() => {
    if (pick.service) papi("get", `/shipping/options?service=${encodeURIComponent(pick.service)}${pick.country ? `&country=${encodeURIComponent(pick.country)}` : ""}`)
      .then((d) => setOpt((o) => ({ ...o, countries: d.countries, weights: d.weights }))).catch(() => {});
  }, [pick.service, pick.country]);
  const costOf = (r) => costs[`${r.service}|${r.country}`]?.[k3(r)];
  const add = (w) => {
    if (rows.some((r) => k3(r) === k3(w))) return toast.info("Already in the list");
    setRows([{ service: w.service, country: w.country, weight: w.weight, w_from: w.w_from, w_to: w.w_to, sale: "" }, ...rows]);
  };
  const save = async () => {
    setBusy(true);
    const keep = rows.filter((x) => Number(x.sale) > 0);
    const left = new Set(keep.map(k3));
    const ship_remove = (c.ship_rates || []).filter((x) => !left.has(k3(x))).map((x) => `${x.service}|${x.country}|${x.weight}`);
    try { const r = await papi("put", `/dropship/clients/${c.id}`, { ship_rates: keep, ship_remove }); toast.success("Shipping prices saved"); setRows(r.ship_rates || []); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const sel = "h-8 rounded-lg border px-2 text-xs";
  return (
    <div className="space-y-2 border-t pt-3" data-testid="ds-ship-list">
      <div className="flex flex-wrap items-center gap-2"><Truck className="h-4 w-4 text-teal-700" /><b className="text-sm">Shipping prices ({rows.length})</b>
        <span className="text-[11px] text-slate-500">filled in by itself when an order's country + mode (and weight) match</span>
        <span className="ml-auto flex items-center gap-1.5"><a href="#" onClick={(e) => { e.preventDefault(); sheetPdf(c); }} className="inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50" data-testid="ds-client-pdf"><FileText className="h-3.5 w-3.5" />Client price sheet (PDF)</a>
          <SheetButtons cid={c.id} kind="shipping" onDone={refresh} /></span></div>
      <div className="flex flex-wrap items-center gap-1.5 rounded-lg bg-slate-50 p-2">
        <select value={pick.service} onChange={(e) => setPick({ service: e.target.value, country: "" })} className={sel} data-testid="ds-ship-service">
          <option value="">Service…</option>{opt.services.map((s) => <option key={s}>{s}</option>)}</select>
        <select value={pick.country} onChange={(e) => setPick({ ...pick, country: e.target.value })} disabled={!pick.service} className={`${sel} max-w-xs`} data-testid="ds-ship-country">
          <option value="">Country…</option>{(opt.countries || []).map((s) => <option key={s}>{s}</option>)}</select>
        {pick.country && (opt.weights || []).map((w) => (
          <button key={k3(w)} onClick={() => add(w)} className="inline-flex items-center gap-1 rounded-full border bg-white px-2 py-0.5 text-[11px] hover:border-teal-400" data-testid={`ds-ship-add-${w.weight}`}>
            <Plus className="h-3 w-3" />{w.weight}{w.cost != null && <span className="text-slate-500">· {inr(w.cost)}</span>}</button>))}
        {pick.country && <ShipMarkup weights={opt.weights} rows={rows} setRows={setRows} />}
      </div>
      <NoCopy className="max-h-72 overflow-auto rounded-xl border" testid="ds-ship-table-wrap">
        <table className="w-full text-xs"><thead className="sticky top-0 bg-slate-50 text-left text-[11px] text-slate-500">
          <tr><th className="px-2 py-1.5">Service</th><th>Country</th><th>Weight</th><th>Cost</th><th>Sale (₹)</th><th>Margin</th><th /></tr></thead>
          <tbody>{rows.map((r, i) => {
            const cost = costOf(r)?.cost;
            return (<tr key={k3(r)} className="border-t" data-testid={`ds-ship-row-${i}`}>
              <td className="px-2 py-1 font-semibold">{r.service || "—"}</td><td>{r.country}</td><td>{r.weight || "—"}</td>
              <td>{costOf(r) ? inr(cost) : <span className="rounded bg-amber-100 px-1 text-[10px] text-amber-800">Not in shipping prices</span>}</td>
              <td><input type="number" value={r.sale} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, sale: e.target.value } : x)))} className="w-24 rounded border px-2 py-1" data-testid={`ds-ship-sale-${i}`} /></td>
              <td><Margin sale={r.sale} cost={cost} testid={`ds-ship-margin-${i}`} /></td>
              <td><button onClick={() => setRows(rows.filter((_, j) => j !== i))} className="text-rose-500"><Trash2 className="h-4 w-4" /></button></td></tr>);
          })}
          {!rows.length && <tr><td colSpan={7} className="px-3 py-4 text-center text-slate-400">No shipping prices yet — pick Service + Country above, or upload the template.</td></tr>}</tbody></table>
      </NoCopy>
      <div className="flex"><button onClick={save} disabled={busy} className="ml-auto inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="ds-ship-save">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Save shipping prices</button></div>
    </div>
  );
};
